/**
 * FOLD — sidebar menu customization pure model (docs/plans/sidebar-menu-customization-fold.md).
 *
 * The ONE place a user's left-nav layout (item order + the single separator's position + collapsed
 * state) is described, validated, and reconciled against the nav items actually on offer. Pure and
 * framework-free — operates on nav-item id strings, NOT React elements — so both the server
 * (`coerceMenuLayout` on write) and the client (`reconcileMenuLayout` on render) share it, mirroring
 * how `coerceWorkingHours` / `coerceWritingStyleProfile` live in core.
 *
 * Model (spec §4): exactly one separator, represented by the {@link SEPARATOR_ID} sentinel inside
 * `order`. Its index IS its position; every id after it is the collapsible ("below") zone. There is
 * NO per-item hide — the separator's collapse is the only hide/show mechanism (spec D5).
 */

/** Sentinel id standing in for the single divider/separator row inside {@link MenuLayout.order}. */
export const SEPARATOR_ID = '__separator__';

/** Current {@link MenuLayout} schema version, bumped when the stored shape changes. */
export const MENU_LAYOUT_VERSION = 1 as const;

export interface MenuLayout {
  /** Schema version for safe future migration. */
  version: typeof MENU_LAYOUT_VERSION;
  /**
   * Ordered nav-item ids as the user arranged them, PLUS exactly one {@link SEPARATOR_ID}. Ids not
   * present here are unknown/new items, appended to the visible zone at reconcile time (spec D7). Ids
   * present but no longer offered (feature turned off) are ignored on render but retained in storage,
   * so re-enabling the feature restores its saved position.
   */
  order: string[];
  /** Whether the below-separator zone is collapsed. Synced across devices (spec D13). */
  collapsed: boolean;
}

/**
 * Default layout, derived from the current hard-coded nav order. The seeded separator sits after the
 * day-to-day surfaces and before the peripheral ones (spec §4.2, FL-4). Feature-gated ids
 * (subscription/finances/fitness) are intentionally absent — when their feature is on they append to
 * the visible zone (spec D7 / {@link reconcileMenuLayout}).
 */
export const DEFAULT_MENU_LAYOUT: MenuLayout = {
  version: MENU_LAYOUT_VERSION,
  order: [
    'journals',
    'pages',
    'initiatives',
    'decisions',
    'reddit',
    'ai-coaching',
    'contacts',
    SEPARATOR_ID,
    'integrations',
    'utilities',
  ],
  collapsed: false,
};

/** Deep-ish clone of the default so callers can freely mutate the returned `order` array. */
export function defaultMenuLayout(): MenuLayout {
  return { version: MENU_LAYOUT_VERSION, order: [...DEFAULT_MENU_LAYOUT.order], collapsed: false };
}

/**
 * Coerce untrusted input (wire payload / stored blob) into a valid {@link MenuLayout}. Defensive by
 * construction — the stored shape is ALWAYS valid after this, so render/persist paths never crash:
 *
 * - non-object / missing → the default layout;
 * - `order` filtered to unique non-empty strings (drops non-strings, dedups so an id never appears
 *   twice and the separator is never duplicated);
 * - guarantees EXACTLY ONE separator: if the input has none, one is appended at the end; if it has
 *   several, only the first survives (dedup);
 * - `collapsed` coerced to boolean (default false);
 * - `version` pinned to the current version.
 *
 * An `order` that ends up with no real items still keeps its separator, which is harmless (the
 * sidebar just renders the separator alone) — reconcile will append the live items anyway.
 */
export function coerceMenuLayout(raw: unknown): MenuLayout {
  if (!raw || typeof raw !== 'object') return defaultMenuLayout();
  const r = raw as Partial<MenuLayout>;
  const rawOrder = Array.isArray(r.order) ? r.order : DEFAULT_MENU_LAYOUT.order;
  const seen = new Set<string>();
  const order: string[] = [];
  for (const entry of rawOrder) {
    if (typeof entry !== 'string') continue;
    const id = entry.trim();
    if (!id) continue;
    if (seen.has(id)) continue; // dedup — also collapses duplicate separators to one
    seen.add(id);
    order.push(id);
  }
  if (!order.includes(SEPARATOR_ID)) order.push(SEPARATOR_ID);
  return {
    version: MENU_LAYOUT_VERSION,
    order,
    collapsed: r.collapsed === true,
  };
}

/** Result of reconciling a stored layout against the ids actually on offer. */
export interface ReconciledMenu {
  /** The effective order (ids + one separator), after appending new ids and dropping stale ones. */
  order: string[];
  /** Index of the separator within {@link order}. */
  separatorIndex: number;
  /** Ids before the separator — always shown. */
  visibleIds: string[];
  /** Ids after the separator — hidden when the zone is collapsed. */
  hiddenIds: string[];
}

/**
 * Reconcile a stored (or absent) layout against the nav-item ids currently available, producing the
 * concrete order to render (spec §4.3). Pure:
 *
 * 1. start from the stored `order` (coerced), or the default when absent;
 * 2. drop ids not in `available` (feature off / removed) — the RAW stored order is untouched;
 * 3. append any available id not already present to the END of the VISIBLE zone, i.e. immediately
 *    before the separator (spec D7 — new items surface where the user notices them but without
 *    disturbing the curated top order);
 * 4. split at the separator into `visibleIds` / `hiddenIds`;
 * 5. guarantee exactly one separator (coerce already ensures this).
 *
 * @param stored the user's saved layout, or undefined for a first-run user.
 * @param available the ids of nav items currently on offer, in their natural (fallback) order.
 */
export function reconcileMenuLayout(stored: MenuLayout | undefined, available: string[]): ReconciledMenu {
  const layout = coerceMenuLayout(stored ?? defaultMenuLayout());
  const availableSet = new Set(available);

  // (2) keep only ids still on offer, plus the separator sentinel.
  const kept = layout.order.filter((id) => id === SEPARATOR_ID || availableSet.has(id));

  // (3) append brand-new available ids (never seen in the stored order) just before the separator,
  // preserving their natural relative order.
  const knownSet = new Set(kept);
  const newcomers = available.filter((id) => !knownSet.has(id));
  let sepIndex = kept.indexOf(SEPARATOR_ID);
  if (sepIndex === -1) {
    // Defensive: coerce guarantees a separator, but if filtering ever removed it, re-append.
    kept.push(SEPARATOR_ID);
    sepIndex = kept.length - 1;
  }
  const order = [...kept.slice(0, sepIndex), ...newcomers, SEPARATOR_ID, ...kept.slice(sepIndex + 1)];

  const separatorIndex = order.indexOf(SEPARATOR_ID);
  return {
    order,
    separatorIndex,
    visibleIds: order.slice(0, separatorIndex),
    hiddenIds: order.slice(separatorIndex + 1),
  };
}

/**
 * Whether the below-separator zone should render collapsed, given the stored preference and the
 * active nav id (spec D9): if the active item lives in the hidden zone, force-expand so the user
 * never loses their place — WITHOUT rewriting the stored `collapsed` value (navigating away restores
 * the preference). `activeId` may be a child id (e.g. `ai-coaching/voice`); its top-level parent
 * (before the first `/`) is what we match against the hidden zone.
 */
export function effectiveCollapsed(
  layout: MenuLayout,
  reconciled: Pick<ReconciledMenu, 'hiddenIds'>,
  activeId: string | undefined,
): boolean {
  if (!layout.collapsed) return false;
  if (!activeId) return true;
  const topLevel = activeId.includes('/') ? activeId.slice(0, activeId.indexOf('/')) : activeId;
  return !reconciled.hiddenIds.includes(topLevel);
}

/**
 * Move the id `activeId` to the slot currently held by `overId`, shifting the rest — the pure
 * equivalent of a drag from one row onto another (mirrors dnd-kit's `arrayMove`, but framework-free
 * and unit-testable without measured layout rects, which jsdom lacks). Returns the input unchanged
 * when either id is absent or they're identical.
 */
export function moveMenuItem(order: string[], activeId: string, overId: string): string[] {
  const from = order.indexOf(activeId);
  const to = order.indexOf(overId);
  if (from === -1 || to === -1 || from === to) return order;
  const next = [...order];
  const [moved] = next.splice(from, 1);
  if (moved === undefined) return order; // unreachable (from is a valid index), but satisfies strict indexing
  next.splice(to, 0, moved);
  return next;
}

/**
 * Apply a full drag-reorder result: the user handed us a new ordering of ids (including the
 * separator). Coerce it so the stored shape stays valid (exactly one separator, deduped). Preserves
 * the collapsed flag.
 */
export function reorderMenuLayout(layout: MenuLayout, nextOrder: string[]): MenuLayout {
  const coerced = coerceMenuLayout({ version: MENU_LAYOUT_VERSION, order: nextOrder, collapsed: layout.collapsed });
  return coerced;
}
